import { useState, useEffect } from 'react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Upload, Truck, DollarSign, TrendingUp, RefreshCw, 
  Pencil, Trash2, FileSpreadsheet, Users, AlertTriangle
} from 'lucide-react';
import { getErrorMessage } from '@/utils/errorHandler';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function OwnerDriverPayouts() {
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [payouts, setPayouts] = useState([]);
  const [totals, setTotals] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState('2026-01');
  const [selectedYear, setSelectedYear] = useState('2025/2026');
  const [financialYears, setFinancialYears] = useState([]);
  
  // Edit dialog state
  const [editingPayout, setEditingPayout] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editForm, setEditForm] = useState({ driver_name: '', total_exvat: '', payout: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchFinancialYears();
  }, []);

  useEffect(() => {
    if (selectedMonth && selectedYear) {
      fetchPayouts();
    }
  }, [selectedMonth, selectedYear]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(amount || 0);
  };

  const fetchFinancialYears = async () => {
    try {
      const response = await axios.get(`${API_URL}/finance/years`);
      const years = response.data?.financial_years || [];
      // Convert to format expected by component
      const formattedYears = years.map(y => ({ year: y }));
      setFinancialYears(formattedYears);
      if (formattedYears.length > 0) {
        setSelectedYear(formattedYears[0].year);
      }
    } catch (error) {
      console.error('Failed to fetch financial years');
    }
  };

  const fetchPayouts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/finance/owner-driver-payouts`, {
        params: { month: selectedMonth, financial_year: selectedYear }
      });
      setPayouts(response.data.payouts || []);
      setTotals(response.data.totals || null);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load payouts'));
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!selectedMonth) {
      toast.error('Please select a month first');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('month', selectedMonth);
    formData.append('financial_year', selectedYear);

    try {
      const response = await axios.post(`${API_URL}/finance/owner-driver-payout/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`Imported ${response.data.imported} driver payouts. Income: ${response.data.income_created}, Expenses: ${response.data.expense_created}`);
      fetchPayouts();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Upload failed'));
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const openEditDialog = (payout) => {
    setEditingPayout(payout);
    setEditForm({
      driver_name: payout.driver_name || '',
      total_exvat: payout.total_exvat?.toString() || '',
      payout: payout.payout?.toString() || ''
    });
    setShowEditDialog(true);
  };

  const saveEdit = async () => {
    if (!editingPayout) return;
    setSaving(true);

    try {
      await axios.put(`${API_URL}/finance/owner-driver-payouts/${editingPayout.id}`, null, {
        params: {
          driver_name: editForm.driver_name || undefined,
          total_exvat: editForm.total_exvat ? parseFloat(editForm.total_exvat) : undefined,
          payout: editForm.payout ? parseFloat(editForm.payout) : undefined
        }
      });
      toast.success('Payout updated');
      setShowEditDialog(false);
      fetchPayouts();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update'));
    } finally {
      setSaving(false);
    }
  };

  const deletePayout = async (payout) => {
    if (!confirm(`Delete payout for ${payout.driver_name}?\n\nThis will also remove the related income and expense entries.`)) return;

    try {
      await axios.delete(`${API_URL}/finance/owner-driver-payouts/${payout.id}`);
      toast.success('Payout deleted');
      fetchPayouts();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete'));
    }
  };

  // Generate month options
  const monthOptions = [];
  for (let y = 2024; y <= 2027; y++) {
    for (let m = 1; m <= 12; m++) {
      const monthStr = `${y}-${m.toString().padStart(2, '0')}`;
      const monthName = new Date(y, m - 1).toLocaleString('en', { month: 'long', year: 'numeric' });
      monthOptions.push({ value: monthStr, label: monthName });
    }
  }

  return (
    <DashboardLayout>
      <div className="space-y-4 max-w-[95%] mx-auto" data-testid="owner-driver-payouts-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Truck className="h-6 w-6 text-blue-600" />
              Owner Driver Payouts
            </h1>
            <p className="text-sm text-muted-foreground">
              Upload and manage owner driver payout data - automatically adds to Income & Expenses
            </p>
          </div>
        </div>

        {/* Controls */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap items-end gap-4">
              <div className="space-y-1">
                <Label>Month</Label>
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  className="flex h-10 w-48 rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  {monthOptions.map(m => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-1">
                <Label>Financial Year</Label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  className="flex h-10 w-40 rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  {financialYears.map(fy => (
                    <option key={fy.year} value={fy.year}>{fy.year}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-1">
                <Label>Upload Excel/CSV</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    onChange={handleUpload}
                    disabled={uploading}
                    className="w-64"
                  />
                  {uploading && <RefreshCw className="h-4 w-4 animate-spin" />}
                </div>
              </div>

              <Button onClick={fetchPayouts} variant="outline" disabled={loading}>
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>

            <div className="mt-3 p-3 bg-blue-50 rounded-lg text-sm">
              <p className="font-medium text-blue-800 mb-1">Expected Excel Columns:</p>
              <p className="text-blue-700">
                <strong>Owner DRIVER</strong> (name), 
                <strong> Sum - TOTAL EXVAT</strong> (revenue ex VAT → adds to Income with 15% VAT), 
                <strong> Sum - OD TOTAL pay out</strong> (payout → adds to Expenses)
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Summary Cards */}
        {totals && totals.count > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-4 text-center">
                <Users className="h-6 w-6 text-blue-600 mx-auto mb-1" />
                <p className="text-xs text-blue-700">Drivers</p>
                <p className="text-xl font-bold text-blue-800">{totals.count}</p>
              </CardContent>
            </Card>

            <Card className="bg-green-50 border-green-200">
              <CardContent className="p-4 text-center">
                <DollarSign className="h-6 w-6 text-green-600 mx-auto mb-1" />
                <p className="text-xs text-green-700">Total Ex VAT</p>
                <p className="text-lg font-bold text-green-800">{formatCurrency(totals.total_exvat)}</p>
              </CardContent>
            </Card>

            <Card className="bg-purple-50 border-purple-200">
              <CardContent className="p-4 text-center">
                <p className="text-xs text-purple-700">+ 15% VAT</p>
                <p className="text-lg font-bold text-purple-800">{formatCurrency(totals.total_vat)}</p>
                <p className="text-xs text-purple-600">= {formatCurrency(totals.total_incl_vat)} Incl</p>
              </CardContent>
            </Card>

            <Card className="bg-red-50 border-red-200">
              <CardContent className="p-4 text-center">
                <p className="text-xs text-red-700">Total Payout</p>
                <p className="text-lg font-bold text-red-800">{formatCurrency(totals.total_payout)}</p>
                <p className="text-xs text-red-600">→ Expenses</p>
              </CardContent>
            </Card>

            <Card className={totals.total_profit >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-orange-50 border-orange-200'}>
              <CardContent className="p-4 text-center">
                <TrendingUp className={`h-6 w-6 mx-auto mb-1 ${totals.total_profit >= 0 ? 'text-emerald-600' : 'text-orange-600'}`} />
                <p className={`text-xs ${totals.total_profit >= 0 ? 'text-emerald-700' : 'text-orange-700'}`}>Profit</p>
                <p className={`text-lg font-bold ${totals.total_profit >= 0 ? 'text-emerald-800' : 'text-orange-800'}`}>
                  {formatCurrency(totals.total_profit)}
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Payouts Table */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5" />
              Owner Driver Payouts - {new Date(selectedMonth + '-01').toLocaleString('en', { month: 'long', year: 'numeric' })}
            </CardTitle>
            <CardDescription>
              TOTAL EXVAT + 15% VAT → Income | PAYOUT → Expenses
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="text-center py-8">
                <RefreshCw className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
              </div>
            ) : payouts.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead>Driver Name</TableHead>
                    <TableHead className="text-right">Total Ex VAT</TableHead>
                    <TableHead className="text-right">VAT (15%)</TableHead>
                    <TableHead className="text-right text-green-700">Income (Incl VAT)</TableHead>
                    <TableHead className="text-right text-red-700">Payout (Expense)</TableHead>
                    <TableHead className="text-right">Profit</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payouts.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell className="font-medium">{p.driver_name}</TableCell>
                      <TableCell className="text-right">{formatCurrency(p.total_exvat)}</TableCell>
                      <TableCell className="text-right text-purple-600">{formatCurrency(p.vat_amount)}</TableCell>
                      <TableCell className="text-right text-green-700 font-medium">{formatCurrency(p.total_incl_vat)}</TableCell>
                      <TableCell className="text-right text-red-700 font-medium">{formatCurrency(p.payout)}</TableCell>
                      <TableCell className={`text-right font-bold ${p.profit >= 0 ? 'text-emerald-600' : 'text-orange-600'}`}>
                        {formatCurrency(p.profit)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEditDialog(p)}>
                            <Pencil className="h-3 w-3" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-600" onClick={() => deletePayout(p)}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {/* Totals Row */}
                  <TableRow className="bg-muted font-bold border-t-2">
                    <TableCell>TOTAL</TableCell>
                    <TableCell className="text-right">{formatCurrency(totals?.total_exvat)}</TableCell>
                    <TableCell className="text-right text-purple-700">{formatCurrency(totals?.total_vat)}</TableCell>
                    <TableCell className="text-right text-green-700">{formatCurrency(totals?.total_incl_vat)}</TableCell>
                    <TableCell className="text-right text-red-700">{formatCurrency(totals?.total_payout)}</TableCell>
                    <TableCell className={`text-right ${totals?.total_profit >= 0 ? 'text-emerald-700' : 'text-orange-700'}`}>
                      {formatCurrency(totals?.total_profit)}
                    </TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <Truck className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>No owner driver payouts for this month</p>
                <p className="text-sm mt-2">Upload an Excel file to import data</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Edit Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Owner Driver Payout</DialogTitle>
              <DialogDescription>
                Update the payout details. Changes will also update related Income and Expense entries.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Driver Name</Label>
                <Input
                  value={editForm.driver_name}
                  onChange={(e) => setEditForm({ ...editForm, driver_name: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Total Ex VAT (R)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editForm.total_exvat}
                    onChange={(e) => setEditForm({ ...editForm, total_exvat: e.target.value })}
                  />
                  <p className="text-xs text-green-600">
                    + 15% VAT = {formatCurrency((parseFloat(editForm.total_exvat) || 0) * 1.15)} → Income
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Payout (R)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editForm.payout}
                    onChange={(e) => setEditForm({ ...editForm, payout: e.target.value })}
                  />
                  <p className="text-xs text-red-600">
                    → Expense
                  </p>
                </div>
              </div>
              <div className="bg-gray-100 p-3 rounded-lg">
                <p className="text-sm">
                  <strong>Profit:</strong> {formatCurrency((parseFloat(editForm.total_exvat) || 0) - (parseFloat(editForm.payout) || 0))}
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
              <Button onClick={saveEdit} disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
